import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { PROXY } from "../../lib/utils";
import { Input, Button, Typography, Card } from "@material-tailwind/react";
import { IoEye, IoEyeOff } from "react-icons/io5";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { apiErrorMessage, apiValidationMessage, passwordIssue, passwordMessage } from "../../lib/passwordPolicy";

export const getValidationSchema = (t) =>
  Yup.object().shape({
    currentPassword: Yup.string().required(t("validation.current-password-required")),
    newPassword: Yup.string().test("password-policy", function (value) {
      const issue = passwordIssue(value);
      return issue ? this.createError({ message: passwordMessage(issue, t) }) : true;
    }),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], t("validation.password-match"))
      .required(t("validation.confirm-password-required")),
  });

const ChangePassword = () => {
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const { t, i18n } = useTranslation();
  const { accessToken, logout } = useAuth();

  const toggleVisibility = (setVisible) => setVisible((prev) => !prev);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(getValidationSchema(t)),
  });

  const onSubmit = async (formData) => {
    try {
      await axios.patch(`${PROXY}/users/me/password`, formData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success(t("toast.password-updated"));
      reset();
      await logout();
    } catch (error) {
      if (!error.response) {
        toast.error(t("toast.internet-error"));
      } else if (error.response.data.errors?.length > 0) {
        error.response.data.errors.forEach((err) => {
          if (["currentPassword", "newPassword", "confirmPassword"].includes(err.path)) {
            setError(err.path, { type: "manual", message: apiValidationMessage(err, t) });
          }
        });
      } else {
        toast.error(apiErrorMessage(error.response.data, t));
      }
    }
  };

  const isRTL = i18n.language === "pr" || i18n.language === "ps";

  return (
    <Card className="w-full shadow-lg border border-gray-100">
      {/* Neutral Header */}
      <div className="p-6 border-b border-gray-200 bg-white rounded-t-lg">
        <Typography variant="h4" className="font-bold text-gray-800">
          {t("Change-Password")}
        </Typography>
        <Typography variant="small" className="text-gray-500 mt-1">
          {t("Update-your-profile-password-below.")}
        </Typography>
      </div>

      <div className="flex flex-col md:flex-row gap-8 p-4 sm:p-6">
        {/* Form */}
        <form
          className="w-full md:w-2/3 space-y-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Current Password */}
          <div>
            <label className={`block mb-1 text-sm font-medium ${isRTL ? "text-right" : "text-left"}`}>
              {t("Current-Password")}
            </label>
            <div className="relative">
              <Input
                type={currentPasswordVisible ? "text" : "password"}
                {...register("currentPassword")}
                className={`w-full ${isRTL ? "text-right" : "text-left"}`}
                color={errors.currentPassword ? "red" : "indigo"}
                size="lg"
                placeholder={t("Current-Password")}
                error={errors?.currentPassword}
                containerProps={{ className: "w-full" }}
              />
              <button
                type="button"
                className={`absolute inset-y-0 ${isRTL ? "left-0 pl-3" : "right-0 pr-3"} flex items-center`}
                onClick={() => toggleVisibility(setCurrentPasswordVisible)}
              >
                {currentPasswordVisible ? (
                  <IoEye className="w-5 h-5 text-indigo-500" />
                ) : (
                  <IoEyeOff className="w-5 h-5 text-indigo-500" />
                )}
              </button>
              <p className="text-red-500 text-xs">{errors.currentPassword?.message}</p>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className={`block mb-1 text-sm font-medium ${isRTL ? "text-right" : "text-left"}`}>
              {t("New-Password")}
            </label>
            <div className="relative">
              <Input
                type={newPasswordVisible ? "text" : "password"}
                {...register("newPassword")}
                className={`w-full ${isRTL ? "text-right" : "text-left"}`}
                color={errors.newPassword ? "red" : "indigo"}
                size="lg"
                placeholder={t("New-Password")}
                error={errors?.newPassword}
                containerProps={{ className: "w-full" }}
              />
              <button
                type="button"
                className={`absolute inset-y-0 ${isRTL ? "left-0 pl-3" : "right-0 pr-3"} flex items-center`}
                onClick={() => toggleVisibility(setNewPasswordVisible)}
              >
                {newPasswordVisible ? (
                  <IoEye className="w-5 h-5 text-indigo-500" />
                ) : (
                  <IoEyeOff className="w-5 h-5 text-indigo-500" />
                )}
              </button>
              <p className="text-red-500 text-xs">{errors.newPassword?.message}</p>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className={`block mb-1 text-sm font-medium ${isRTL ? "text-right" : "text-left"}`}>
              {t("Confirm-New-Password")}
            </label>
            <div className="relative">
              <Input
                type={confirmPasswordVisible ? "text" : "password"}
                {...register("confirmPassword")}
                className={`w-full ${isRTL ? "text-right" : "text-left"}`}
                color={errors.confirmPassword ? "red" : "indigo"}
                size="lg"
                placeholder={t("Confirm-New-Password")}
                error={errors?.confirmPassword}
                containerProps={{ className: "w-full" }}
              />
              <button
                type="button"
                className={`absolute inset-y-0 ${isRTL ? "left-0 pl-3" : "right-0 pr-3"} flex items-center`}
                onClick={() => toggleVisibility(setConfirmPasswordVisible)}
              >
                {confirmPasswordVisible ? (
                  <IoEye className="w-5 h-5 text-indigo-500" />
                ) : (
                  <IoEyeOff className="w-5 h-5 text-indigo-500" />
                )}
              </button>
              <p className="text-red-500 text-xs">{errors.confirmPassword?.message}</p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <Button
              type="submit"
              size="md"
              variant="gradient"
              color="indigo"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-6 py-2 font-semibold"
            >
              {isSubmitting ? t("Updating") + "..." : t("Update-Password")}
            </Button>
          </div>
        </form>

        {/* Requirements Sidebar */}
        <div className="w-full md:w-1/3">
          <h3 className="text-sm font-semibold">{t("Password-Requirements")}</h3>
          <p className="mt-2 text-sm text-gray-600">{t("validation.password_hint")}</p>
        </div>
      </div>
    </Card>
  );
};

export default ChangePassword;
